import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Active, DataRef, Over } from '@dnd-kit/core';
import { ColumnDragData } from '@/app/dashboard/kanban/_components/board-column';
import { TaskDragData } from '@/app/dashboard/kanban/_components/task-card';

type DraggableData = ColumnDragData | TaskDragData;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function hasDraggableData<T extends Active | Over>(
  entry: T | null | undefined
): entry is T & {
  data: DataRef<DraggableData>;
} {
  if (!entry) {
    return false;
  }

  const data = entry.data.current;

  if (data?.type === 'Column' || data?.type === 'Task') {
    return true;
  }

  return false;
}

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: 'accurate' | 'normal';
  } = {}
) {
  const { decimals = 0, sizeType = 'normal' } = opts;

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const accurateSizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${sizeType === 'accurate' ? accurateSizes[i] ?? 'Bytest' : sizes[i] ?? 'Bytes'
    }`;
}

// 默认 30 天
export const setCookie = (name: string, value: string, days: number = 30) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = '; expires=' + date.toUTCString();
  document.cookie = name + '=' + value + expires + '; path=/';
}

export const getCookie = (name: string) => {
  let value = '; ';
  if (document) {
    value += document.cookie;
  }
  const parts = value.split('; ' + name + '=');
  if (parts.length === 2) return parts.pop()?.split(';').shift();
}

export const removeCookie = (name: string) => {
  setCookie(name, '', -1);
}

export function obj2Query(obj: any) {
  let query = '';
  if (obj && Object.keys(obj).length > 0) {
    Object.keys(obj).forEach(key => {
      query += `${key}=${encodeURIComponent((obj[`${key}`] + '').trim())}&`
    })
    return query.slice(0, -1)
  } else {
    return '';
  }
}

/**
 * 验证 IPv4 地址的有效性（使用正则表达式）
 * @param {string} ip - 要验证的 IP 地址
 * @returns {boolean} - 如果有效则返回 true，否则返回 false
 */
export function isValidIP(ip: string): boolean {
  const regex = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)$/;
  return regex.test(ip);
}

/**
 * 将 IP 地址转换为数字。
 * @param {string} ip - 要转换的 IP 地址。
 * @returns {number} - 转换后的数字表示。
 */
function ipToNumber(ip: string): number {
  return ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0;
}
/**
 * 将数字转换为 IP 地址。
 * @param {number} num - 要转换的数字。
 * @returns {string} - 转换后的 IP 地址。
 */
function numberToIP(num: number): string {
  return [
    (num >>> 24) & 0xFF,
    (num >>> 16) & 0xFF,
    (num >>> 8) & 0xFF,
    num & 0xFF,
  ].join('.');
}


/**
 * 获取两个 IP 之间的所有 IP 地址。
 * @param {string} startIP - 起始 IP 地址。
 * @param {string} endIP - 结束 IP 地址。
 * @returns {object} - 返回包含错误信息和数据的对象。
 *                    { err: string|null, data: string[]|null }
 */
export function getAllIPInSegment(startIP: string, endIP: string): { err: null | string, data: null | string[] } {
  if (!isValidIP(startIP) || !isValidIP(endIP)) {
    return {
      err: 'IP 格式不正确！',
      data: null
    }
  }

  const octets1 = startIP.split('.').map(Number);
  const octets2 = endIP.split('.').map(Number);

  for (let i = 0; i < 3; i++) {
    if (octets1[i] !== octets2[i]) {
      return {
        err: '起止 IP 不属于同一 IP 段！',
        data: null
      };
    }
  }

  const startNum = ipToNumber(startIP);
  const endNum = ipToNumber(endIP);
  if (startNum > endNum) {
    return {
      err: '起始 IP 大于结束 IP！',
      data: null
    };
  }
  const data = [];
  if (startNum === endNum) {
    data.push(startIP);
  } else {
    for (let num = startNum; num <= endNum; num++) {
      data.push(numberToIP(num));
    }
  }
  return {
    err: null,
    data
  }
}

export function isSameSubnet(startIP: string, endIP: string): { err: null | string, data: null | boolean } {
  if (!isValidIP(startIP) || !isValidIP(endIP)) {
    return {
      err: 'IP 格式不正确！',
      data: null
    }
  }

  const octets1 = startIP.split('.').map(Number);
  const octets2 = endIP.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    if (octets1[i] !== octets2[i]) {
      return {
        err: '起止 IP 不属于同一 IP 段！',
        data: null
      };
    }
  }

  const startNum = ipToNumber(startIP);
  const endNum = ipToNumber(endIP);
  if (startNum > endNum) {
    return {
      err: '起始 IP 大于结束 IP！',
      data: null
    };
  }

  return {
    err: null,
    data: true
  }
}

export function areValueEqualAdvanced(arr1: number[], arr2: number[]) {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
  if (arr1.length !== arr2.length) return false;

  // 哈希表计数器
  const counter = new Map();

  // 统计 arr1 元素
  for (const x of arr1) {
    const key = Number.isNaN(x) ? 'NaN' : x; // 统一处理 NaN
    counter.set(key, (counter.get(key) || 0) + 1);
  }

  // 检查 arr2 元素
  for (const x of arr2) {
    const key = Number.isNaN(x) ? 'NaN' : x;
    const count = counter.get(key);
    if (!count) return false; // 不存在或计数归零
    counter.set(key, count - 1);
  }

  return true;
}